import React, { createContext, useContext, useState } from "react";
import { LuggageCommissionBookingType } from "../components/CommissionBookingTable";

export interface LuggageBookingData {
  id: string;
  bookingType: string;
  bookingMode: string;
  operatorType: string;
  operatorId: string;
  operatorName: string;
  operatorContactName: string;
  operatorContactNumber: string;
  remark: string;
  contractAmount: string;
  gstPercentage: string;
  journeyType: string;
  receivedAdvancePayment: boolean;
  paymentDetails: {
    id: string;
    paymentMode: string;
    paymentAmount: string;
    gstPercentage: string;
    paymentLocation: string;
    paymentDate: string;
    type: string;
    paymentReceivedFrom: string;
    changed: boolean;
    createdAt?: string;
    deletedAt?: string;
  }[];
  deletedPaymentDetails: {
    id: string;
    paymentMode: string;
    paymentAmount: string;
    gstPercentage: string;
    paymentLocation: string;
    paymentDate: string;
    type: string;
    paymentReceivedFrom: string;
    changed: boolean;
    createdAt?: string;
    deletedAt?: string;
  }[];
  changed: boolean;
  noIncome: boolean;
  renew: boolean;
  createdAt?: string;
  deletedAt?: string | null;
  isEdit?: boolean;
  commissionBookings?: LuggageCommissionBookingType[];
}

interface LuggageBookingContextType {
  luggageBookingData: LuggageBookingData[];
  setLuggageBookingData: React.Dispatch<
    React.SetStateAction<LuggageBookingData[]>
  >;
  updateLuggageBookingData: (
    index: number,
    section: keyof LuggageBookingData,
    data: any
  ) => void;
  resetLuggageBookingData: () => void;
}

const initialLuggageBookingData: LuggageBookingData = {
  id: "",
  bookingType: "LUGGAGE",
  bookingMode: "CONTRACT",
  operatorType: "INHOUSE",
  operatorId: "",
  operatorName: "",
  operatorContactName: "",
  operatorContactNumber: "",
  remark: "",
  contractAmount: "",
  gstPercentage: "",
  journeyType: "START",
  receivedAdvancePayment: false,
  paymentDetails: [],
  deletedPaymentDetails: [],
  changed: false,
  noIncome: false,
  renew: false,
  createdAt: "",
  deletedAt: "",
  isEdit: false,
};

const LuggageBookingContext = createContext<
  LuggageBookingContextType | undefined
>(undefined);

export const LuggageBookingProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [luggageBookingData, setLuggageBookingData] = useState<
    LuggageBookingData[]
  >([]);

  const updateLuggageBookingData = (
    index: number,
    section: keyof LuggageBookingData,
    data: any
  ) => {
    setLuggageBookingData((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const newData = [...prev];
      newData[index] = {
        ...newData[index],
        [section]: data,
      };
      return newData;
    });
  };

  const resetLuggageBookingData = () => {
    setLuggageBookingData([initialLuggageBookingData]);
  };

  return (
    <LuggageBookingContext.Provider
      value={{
        luggageBookingData,
        setLuggageBookingData,
        updateLuggageBookingData,
        resetLuggageBookingData,
      }}
    >
      {children}
    </LuggageBookingContext.Provider>
  );
};

export const useLuggageBookingContext = () => {
  const context = useContext(LuggageBookingContext);
  if (context === undefined) {
    throw new Error(
      "useLuggageBookingContext must be used within a LuggageBookingProvider"
    );
  }
  return context;
};
